import { useState, useEffect } from 'react';
import { usePlan } from '../hooks/usePlan';
import { isNative } from '../utils/native';
import { useDisplayName } from '../hooks/useDisplayName';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useOwner } from '../hooks/useOwner';
import { formatCurrency, formatDate, getExpiryStatus } from '../utils/dateUtils';
import { buildVehicleAlerts } from '../utils/alertUtils';
import {
  Car, Shield, Receipt, Wrench, Bell,
  TrendingUp, TrendingDown, ArrowRight, BarChart2
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, BarChart, Bar
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: 'rgba(10,15,30,0.95)', border: '1px solid rgba(79,142,247,0.3)', borderRadius: 12, padding: '10px 14px', backdropFilter: 'blur(20px)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#4f8ef7' }}>{formatCurrency(payload[0].value)}</div>
      </div>
    );
  }
  return null;
};

export const Dashboard = () => {
  const { isTrialing, trialDaysLeft, plan, isReadOnly } = usePlan();
  const { user } = useAuth();
  const { firstName: heroName } = useDisplayName();
  // Support-access requests are an ACCOUNT/SECURITY matter, deliberately kept
  // separate from the vehicle expiry alerts below — a consent decision should not
  // compete for attention with "road tax due in 12 days", and must not be dismissible.
  const [pendingAccess, setPendingAccess] = useState([]);
  const { ownerId } = useOwner();
  const navigate = useNavigate();
  const [data, setData] = useState({ vehicles: [], insurance: [], roadTax: [], mechanical: [], services: [], expenses: [], valuations: [] });
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const [totalAllCosts, setTotalAllCosts] = useState(0);
  const [spendingRange, setSpendingRange] = useState(99);

  useEffect(() => {
    if (!ownerId) return;
    const load = async () => {
      const [v, ins, rt, mech, svc, exp, insRec, rtRec, mechRec, svcRec, log, vals] = await Promise.all([
        supabase.from('vehicles').select('*, vehicle_photos(*)').in('status', ['active','inactive','classic','daneira','immobilized','dokimi_test','service','restoration']),
        supabase.from('insurance_records').select('*'),
        supabase.from('road_tax_records').select('*'),
        supabase.from('mechanical_reports').select('*'),
        supabase.from('service_records').select('*'),
        supabase.from('expenses').select('*').order('expense_date', { ascending: false }),
        supabase.from('insurance_records').select('cost, start_date'),
        supabase.from('road_tax_records').select('amount_paid, payment_date'),
        supabase.from('mechanical_reports').select('cost, report_date'),
        supabase.from('service_records').select('cost, service_date'),
        supabase.from('vehicle_logbook').select('*').order('entry_date', { ascending: false }),
        supabase.from('vehicle_valuations').select('vehicle_id, purchase_price, current_value, valuation_comparables'),
      ]);
      setData({
        vehicles: v.data || [],
        insurance: ins.data || [],
        roadTax: rt.data || [],
        mechanical: mech.data || [],
        services: svc.data || [],
        expenses: exp.data || [],
        logbook: log.data || [],
        valuations: vals.data || [],
      });

      // Build combined all costs for grand total
      const allCosts = [
        ...(exp.data || []).map(r => Number(r.amount || 0)),
        ...(insRec.data || []).filter(r => r.cost).map(r => Number(r.cost || 0)),
        ...(rtRec.data || []).filter(r => r.amount_paid).map(r => Number(r.amount_paid || 0)),
        ...(mechRec.data || []).filter(r => r.cost).map(r => Number(r.cost || 0)),
        ...(svcRec.data || []).filter(r => r.cost).map(r => Number(r.cost || 0)),
      ];
      setTotalAllCosts(allCosts.reduce((s, v) => s + v, 0));

      setLoading(false);
    };
    load();
  }, [ownerId]);

  useEffect(() => {
    if (!user) return;
    supabase.from('user_profiles').select('full_name').eq('id', user.id).single().then(({ data }) => {
      if (data?.full_name) setUserName(data.full_name);
      else setUserName(user.email?.split('@')[0] || 'there');
    });
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    let stop = false;
    const check = () =>
      supabase.from('support_access')
        .select('id, reason, requested_at')
        .eq('owner_id', user.id).eq('status', 'pending')
        .order('requested_at', { ascending: false })
        .then(({ data, error }) => { if (!stop && !error) setPendingAccess(data || []); });
    check();
    const timer = setInterval(check, 60000);
    return () => { stop = true; clearInterval(timer); };
  }, [user?.id]);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  const { vehicles, insurance, roadTax, mechanical, services, expenses, valuations } = data;

  // Build alerts
  const allAlerts = buildVehicleAlerts(vehicles, insurance, roadTax, mechanical, services);
  const criticalAlerts = allAlerts.filter(a => a.status === 'expired' || a.status === 'critical' || a.status === 'missing' || a.status === 'warning');

  // Expense chart data
  const monthlyExp = {};
  expenses.forEach(e => {
    const month = e.expense_date?.slice(0, 7);
    if (month) monthlyExp[month] = (monthlyExp[month] || 0) + Number(e.amount || 0);
  });
  const chartData = Object.entries(monthlyExp).sort(([a], [b]) => a.localeCompare(b)).slice(spendingRange === 99 ? 0 : -spendingRange).map(([month, total]) => ({
    month: new Date(month + '-01').toLocaleDateString('en', { month: 'short' }),
    total,
  }));

  const totalExpenses = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const thisMonthExp = expenses.filter(e => e.expense_date?.startsWith(new Date().toISOString().slice(0, 7))).reduce((s, e) => s + Number(e.amount || 0), 0);

  const vehiclesByStatus = vehicles.reduce((acc, v) => { acc[v.status] = (acc[v.status] || 0) + 1; return acc; }, {});

  // Portfolio summary — mirrors Portfolio.js's own headline calculations exactly.
  // Two vehicle sets, same distinction Portfolio.js draws:
  //  · "valued"     = any vehicle with a current value → drives the fleet total
  //    and the "X of Y valued" count.
  //  · "comparable" = has BOTH an invested figure (purchase + restoration) AND a
  //    current value → the only set that feeds invested/gain/return%/net return,
  //    so costs on an unvalued car are never measured against a gain it was never
  //    credited for.
  // Restoration is capital, not a running cost, and must be added to invested
  // capital here exactly as Portfolio.js does — this mirror previously summed
  // only purchase_price, silently understating "Invested" and overstating gain
  // for any vehicle with restoration spend (the same bug Portfolio.js's own
  // accounting-correction pass fixed on 2026-07-28, just never ported here).
  // Net Return (gain minus running costs) was missing from this card entirely.
  const portfolio = (() => {
    const vals = (valuations || []).filter(v => vehicles.some(x => x.id === v.vehicle_id));

    const restorationByVehicle = {};
    expenses.forEach(e => {
      if (e.category === 'Restoration') {
        restorationByVehicle[e.vehicle_id] = (restorationByVehicle[e.vehicle_id] || 0) + Number(e.amount || 0);
      }
    });
    const runningCostByVehicle = {};
    const addCost = (rows, amountKey) => rows.forEach(r => {
      if (!r.vehicle_id) return;
      runningCostByVehicle[r.vehicle_id] = (runningCostByVehicle[r.vehicle_id] || 0) + Number(r[amountKey] || 0);
    });
    addCost(expenses.filter(e => e.category !== 'Restoration'), 'amount');
    addCost(insurance, 'cost');
    addCost(roadTax, 'amount_paid');
    addCost(services, 'cost');
    addCost(mechanical, 'cost');

    const withInvested = vals.map(v => {
      const purchasePrice = Number(v.purchase_price) || 0;
      const restorationSpend = restorationByVehicle[v.vehicle_id] || 0;
      const investedCapital = purchasePrice > 0 ? purchasePrice + restorationSpend : 0;
      const currentValue = Number(v.current_value) || 0;
      return { ...v, purchasePrice, restorationSpend, investedCapital, currentValue };
    });

    const valued     = withInvested.filter(v => v.currentValue > 0);
    const comparable = valued.filter(v => v.investedCapital > 0);

    const fleetValue      = valued.reduce((s, v) => s + v.currentValue, 0);
    const invested        = comparable.reduce((s, v) => s + v.investedCapital, 0);
    const comparableValue = comparable.reduce((s, v) => s + v.currentValue, 0);
    const gain    = comparableValue - invested;
    const gainPct = invested > 0 ? (gain / invested) * 100 : 0;
    const runningCosts = comparable.reduce((s, v) => s + (runningCostByVehicle[v.vehicle_id] || 0), 0);
    const netReturn = gain - runningCosts;

    // Per-vehicle breakdown so the Dashboard can show each car's estimated value
    // without a trip to Portfolio. Sorted by value, highest first.
    const byVehicle = valued
      .map(v => {
        const veh = vehicles.find(x => x.id === v.vehicle_id);
        return {
          id: v.vehicle_id,
          name: veh ? `${veh.brand} ${veh.model}` : 'Unknown vehicle',
          reg: veh?.registration_number || '',
          value: v.currentValue,
          gainPct: v.investedCapital > 0 ? ((v.currentValue - v.investedCapital) / v.investedCapital) * 100 : null,
          unbacked: v.valuation_comparables === 0,
        };
      })
      .sort((a, b) => b.value - a.value);

    return {
      valuedCount: valued.length,
      invested, currentValue: fleetValue, gain, gainPct, netReturn, byVehicle,
      unbackedCount: vals.filter(v => v.valuation_comparables === 0 && Number(v.current_value) > 0).length,
    };
  })();

  const firstName = user?.email?.split('@')[0] || 'there';

  return (
    <div className="page">

      {isNative && (
        <div className="m-home-header m-dash-hero">
          <div className="m-home-greet">Hey {heroName || 'there'}</div>
          <div className="m-home-sub">Here's your dashboard today</div>
        </div>
      )}

      {pendingAccess.length > 0 && (
        <div style={{
          background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.4)',
          borderRadius: 10, padding: '14px 18px', marginBottom: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <span style={{ fontSize: 20, flexShrink: 0 }}>🔐</span>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: '#f97316', marginBottom: 2 }}>
                {pendingAccess.length} support access request{pendingAccess.length !== 1 ? 's' : ''} awaiting your approval
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                Car Collector Studio support cannot view your data unless you approve it.
                {pendingAccess[0]?.reason ? ` Reason: ${pendingAccess[0].reason}` : ''}
              </div>
            </div>
          </div>
          <button className="btn btn-sm"
            style={{ background: 'linear-gradient(135deg,#f97316,#eab308)', color: 'white', border: 'none', fontSize: 12, flexShrink: 0 }}
            onClick={() => navigate('/settings')}>
            Review request{pendingAccess.length !== 1 ? 's' : ''} →
          </button>
        </div>
      )}

      {isReadOnly && (
        <div style={{
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)',
          borderRadius: 10, padding: '16px 20px', marginBottom: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#ef4444', marginBottom: 3 }}>
              Your free trial has ended — your account is read-only
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.5 }}>
              Your fleet, documents and valuations are all still here and always will be.
              {isNative ? ' Manage your subscription on the Car Collector Studio website to start adding and editing again.' : ' Choose a plan to start adding and editing again.'}
            </div>
          </div>
          {!isNative && (
            <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }}
              onClick={() => navigate('/settings')}>
              Choose a plan →
            </button>
          )}
        </div>
      )}

      {isTrialing && trialDaysLeft <= 7 && (
        <div style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: 10, padding: '12px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#eab308' }}>⏳ Your free trial ends in <strong>{trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''}</strong> {isNative ? '' : '— upgrade to keep full access'}</span>
          {!isNative && <button className="btn btn-sm" style={{ background: 'linear-gradient(135deg, #4f8ef7, #7c3aed)', color: 'white', border: 'none', fontSize: 12 }} onClick={() => navigate('/settings')}>Upgrade now →</button>}
        </div>
      )}
      {/* Stat cards */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        {[
          { label: 'Total Vehicles', value: vehicles.length, icon: Car, color: '#4f8ef7', sub: `${vehiclesByStatus.active || 0} active`, trend: null, path: '/vehicles' },
          { label: 'Active Alerts', value: criticalAlerts.length, icon: Bell, color: criticalAlerts.length > 0 ? '#ef4444' : '#22c55e', sub: criticalAlerts.length > 0 ? 'Need attention' : 'All clear', trend: null, path: '/alerts' },
          { label: 'This Month', value: formatCurrency(thisMonthExp), icon: TrendingUp, color: '#a855f7', sub: 'Total expenses', trend: null, path: '/reports' },
          { label: 'Total Expenses', value: formatCurrency(totalAllCosts), icon: Receipt, color: '#06b6d4', sub: 'All time', trend: null, path: '/expenses' },
        ].map((item, i) => (
          <div key={i} className="stat-card" onClick={() => item.path && navigate(item.path)} style={{ cursor: item.path ? 'pointer' : 'default' }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: 100, height: 100, background: `radial-gradient(circle at top right, ${item.color}15, transparent 70%)`, pointerEvents: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: `${item.color}18`, border: `1px solid ${item.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${item.color}20` }}>
                <item.icon size={18} style={{ color: item.color }} />
              </div>
              {item.trend && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: item.trend === 'up' ? '#ef4444' : '#22c55e' }}>
                  {item.trend === 'up' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                </div>
              )}
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: -0.5, color: 'var(--text)', marginBottom: 4 }}>{item.value}</div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text3)', marginBottom: 2 }}>{item.label}</div>
            <div style={{ fontSize: 12, color: item.color, fontWeight: 500 }}>{item.sub}</div>
          </div>
        ))}
      </div>

      {/* Logbook widgets */}
      {(() => {
        const logbook = data.logbook || [];
        const now = new Date();
        const vehicleMap = {};
        vehicles.forEach(v => { vehicleMap[v.id] = v; });

        // Latest entry per vehicle to determine "currently out" status
        const latestByVehicle = {};
        logbook.forEach(e => {
          if (!latestByVehicle[e.vehicle_id] || new Date(e.entry_date) > new Date(latestByVehicle[e.vehicle_id].entry_date)) {
            latestByVehicle[e.vehicle_id] = e;
          }
        });

        const currentlyOut = Object.values(latestByVehicle).filter(e =>
          e.expected_return && !e.actual_return && new Date(e.expected_return) >= now && e.status !== 'Completed' && e.status !== 'Cancelled'
        );
        const overdueReturns = Object.values(latestByVehicle).filter(e =>
          e.expected_return && !e.actual_return && new Date(e.expected_return) < now && e.status !== 'Completed' && e.status !== 'Cancelled'
        );
        const recentMileage = logbook.filter(e => e.mileage_diff).slice(0, 5);
        const inService = logbook.filter(e =>
          ['Service','Maintenance'].includes(e.entry_type) && ['Scheduled','In Progress','Awaiting Invoice','Awaiting Return'].includes(e.status)
        );

        if (logbook.length === 0) return null;

        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 24 }}>
            <div className="card" onClick={() => navigate('/vehicles')} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--tex3)' }}>Currently Out</span>
                <span className="badge badge-blue">{currentlyOut.length}</span>
              </div>
              {currentlyOut.length === 0 ? <div style={{ fontSize: 12, color: 'var(--text3)' }}>No vehicles currently out</div> : currentlyOut.slice(0,3).map(e => (
                <div key={e.id} style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
                  {vehicleMap[e.vehicle_id]?.brand} {vehicleMap[e.vehicle_id]?.model} — {e.driver_name || 'Unknown driver'}
                </div>
              ))}
            </div>

            <div className="card" onClick={() => navigate('/vehicles')} style={{ cursor: 'pointer', borderColor: overdueReturns.length > 0 ? 'rgba(239,68,68,0.3)' : undefined }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text)' }}>Overdue Returns</span>
                <span className={`badge ${overdueReturns.length > 0 ? 'badge-red' : 'badge-green'}`}>{overdueReturns.length}</span>
              </div>
              {overdueReturns.length === 0 ? <div style={{ fontSize: 12, color: 'var(--text3)' }}>All vehicles returned on time</div> : overdueReturns.slice(0,3).map(e => (
                <div key={e.id} style={{ fontSize: 12, color: 'var(--red)', marginBottom: 4 }}>
                  {vehicleMap[e.vehicle_id]?.brand} {vehicleMap[e.vehicle_id]?.model} — expected {formatDate(e.expected_return)}
                </div>
              ))}
            </div>

            <div className="card" onClick={() => navigate('/vehicles')} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text)' }}>Recent Mileage Updates</span>
                <span className="badge badge-blue">{recentMileage.length}</span>
              </div>
              {recentMileage.length === 0 ? <div style={{ fontSize: 12, color: 'var(--text3)' }}>No recent mileage updates</div> : recentMileage.map(e => (
                <div key={e.id} style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
                  {vehicleMap[e.vehicle_id]?.brand} {vehicleMap[e.vehicle_id]?.model} — +{e.mileage_diff} km
                </div>
              ))}
            </div>

            <div className="card" onClick={() => navigate('/vehicles')} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text)' }}>Vehicles In Service</span>
                <span className="badge badge-orange">{inService.length}</span>
              </div>
              {inService.length === 0 ? <div style={{ fontSize: 12, color: 'var(--text3)' }}>No vehicles in service</div> : inService.slice(0,3).map(e => (
                <div key={e.id} style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
                  {vehicleMap[e.vehicle_id]?.brand} {vehicleMap[e.vehicle_id]?.model} — {e.status}
                </div>
              ))}
            </div>
          </div>
        );
      })()}
      
      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }} className="dashboard-grid-2">

        {/* Spending chart */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 17 }}>Spending Overview</div>
              <div style={{ fontSize: 14, color: 'var(--text2)' }}>Monthly expenses across all vehicles</div>
            </div>
            <select
              value={spendingRange}
              onChange={e => setSpendingRange(Number(e.target.value))}
              style={{
                padding: '6px 12px', borderRadius: 8, fontSize: 12,
                background: 'var(--surface2)', border: '1px solid var(--border)',
                color: 'var(--text2)', fontFamily: 'DM Sans, sans-serif',
                cursor: 'pointer', outline: 'none',
              }}
            >
              <option value={1}>Last 1 month</option>
              <option value={3}>Last 3 months</option>
              <option value={6}>Last 6 months</option>
              <option value={12}>Last 12 months</option>
              <option value={99}>All time</option>
            </select>
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4f8ef7" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#4f8ef7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `€${v}`} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="total" stroke="#4f8ef7" strokeWidth={2.5} fill="url(#spendGrad)" dot={false} activeDot={{ r: 6, fill: '#4f8ef7', stroke: 'rgba(79,142,247,0.3)', strokeWidth: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 13 }}>No expense data yet</div>
          )}
        </div>

        {/* Fleet breakdown */}
        <div className="card" onClick={() => navigate('/vehicles')} style={{ cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ fontWeight: 700, fontSize: 17 }}>Fleet Status</div>
            <ArrowRight size={14} style={{ color: 'var(--text3)' }} />
          </div>
          <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 20 }}>Vehicles by status</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }} className="dashboard-grid-2">
            {[
              { label: 'Active', key: 'active', color: '#22c55e' },
              { label: 'Classic', key: 'classic', color: '#eab308' },
              { label: 'Inactive', key: 'inactive', color: '#6b7280' },
              { label: 'Immobilized', key: 'immobilized', color: '#ef4444' },
              { label: 'Other', key: 'other', color: '#a855f7' },
            ].map(item => {
              const count = item.key === 'other'
                ? vehicles.filter(v => !['active','classic','inactive','immobilized'].includes(v.status)).length
                : (vehiclesByStatus[item.key] || 0);
              const pct = vehicles.length > 0 ? (count / vehicles.length) * 100 : 0;
              if (count === 0) return null;
              // "Other" lumps together several statuses (restoration, service, company,
              // dealer plates, ...) — there's no single section to jump to, so it just
              // opens the Vehicles page. Every other row maps to one real status group.
              return (
                <div
                  key={item.key}
                  onClick={e => {
                    e.stopPropagation();
                    navigate(item.key === 'other' ? '/vehicles' : `/vehicles?category=${item.key}`);
                  }}
                  style={{ cursor: 'pointer' }}
                  title={`View ${item.label} vehicles`}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 14, color: 'var(--text2)', fontWeight: 500 }}>{item.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: item.color }}>{count}</span>
                  </div>
                  <div style={{ height: 6, background: 'rgba(255,255,255,0.05)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${item.color}, ${item.color}88)`, borderRadius: 3, transition: 'width 0.6s ease', boxShadow: `0 0 8px ${item.color}44` }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quick stats */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { label: 'Insurance Active', value: insurance.filter(r => getExpiryStatus(r.expiry_date)?.status !== 'expired').length, color: '#4f8ef7' },
              { label: 'Tax Up to Date', value: roadTax.filter(r => getExpiryStatus(r.expiry_date)?.status !== 'expired').length, color: '#22c55e' },
            ].map((item, i) => (
              <div key={i} style={{ textAlign: 'center', padding: '10px', background: 'var(--surface2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: item.color }}>{item.value}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Alerts + Vehicles row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }} className="dashboard-grid-2">

        {/* Critical alerts */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Bell size={14} style={{ color: '#ef4444' }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 17 }}>Active Alerts</div>
                <div style={{ fontSize: 14, color: 'var(--text3)' }}>{criticalAlerts.length} requiring action</div>
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/alerts')} style={{ fontSize: 11 }}>
              View all <ArrowRight size={11} />
            </button>
          </div>

          {criticalAlerts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text3)', fontSize: 13 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
              All documents are up to date
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {criticalAlerts.slice(0, 5).map((alert, i) => {
                const v = vehicles.find(v => v.id === alert.vehicleId);
                const isExpired = alert.status === 'expired';
                const color = isExpired ? '#ef4444' : '#f97316';
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10, background: `${color}10`, border: `1px solid ${color}25`, cursor: 'pointer', transition: 'var(--transition)' }} onClick={() => navigate('/alerts')}
                    onMouseEnter={e => e.currentTarget.style.background = `${color}18`}
                    onMouseLeave={e => e.currentTarget.style.background = `${color}10`}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, boxShadow: `0 0 8px ${color}`, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{v?.registration_number} — {v?.brand} {v?.model}</div>
                      <div style={{ fontSize: 13, color: 'var(--text2)' }}>{alert.title}</div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: `${color}20`, color, border: `1px solid ${color}40`, flexShrink: 0 }}>
                      {isExpired ? 'EXPIRED' : 'CRITICAL'}
                    </span>
                  </div>
                );
              })}
              {criticalAlerts.length > 5 && (
                <button className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={() => navigate('/alerts')}>
                  +{criticalAlerts.length - 5} more alerts
                </button>
              )}
            </div>
          )}
        </div>

        {/* Portfolio summary */}
        <div className="card" onClick={() => navigate('/portfolio')} style={{ cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrendingUp size={14} style={{ color: '#22c55e' }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 17 }}>Portfolio</div>
                <div style={{ fontSize: 14, color: 'var(--text3)' }}>
                  {portfolio.valuedCount} of {vehicles.length} vehicles valued
                </div>
              </div>
            </div>
            <ArrowRight size={14} style={{ color: 'var(--text3)' }} />
          </div>

          {portfolio.valuedCount === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text3)', fontSize: 13 }}>
              No valuations yet — add purchase and current values in Car Intelligence to track performance.
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                <div style={{ padding: '12px 14px', background: 'var(--surface2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Total Fleet Value</div>
                  <div style={{ fontSize: 19, fontWeight: 800, color: '#4f8ef7' }}>{formatCurrency(portfolio.currentValue)}</div>
                </div>
                <div style={{ padding: '12px 14px', background: 'var(--surface2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Invested</div>
                  <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--text)' }}>{formatCurrency(portfolio.invested)}</div>
                </div>
              </div>

              <div style={{ padding: '14px', marginBottom: 10, background: portfolio.gain >= 0 ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${portfolio.gain >= 0 ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {portfolio.gain >= 0 ? <TrendingUp size={16} style={{ color: '#22c55e' }} /> : <TrendingDown size={16} style={{ color: '#ef4444' }} />}
                  <span style={{ fontSize: 13, color: 'var(--text2)' }}>Total gain / loss</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: portfolio.gain >= 0 ? '#22c55e' : '#ef4444' }}>
                    {portfolio.gain >= 0 ? '+' : ''}{formatCurrency(portfolio.gain)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                    {portfolio.gainPct >= 0 ? '+' : ''}{portfolio.gainPct.toFixed(1)}%
                  </div>
                </div>
              </div>

              <div style={{ padding: '14px', background: portfolio.netReturn >= 0 ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${portfolio.netReturn >= 0 ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <BarChart2 size={16} style={{ color: portfolio.netReturn >= 0 ? '#22c55e' : '#ef4444' }} />
                  <span style={{ fontSize: 13, color: 'var(--text2)' }}>Net return</span>
                </div>
                <div style={{ fontSize: 17, fontWeight: 800, color: portfolio.netReturn >= 0 ? '#22c55e' : '#ef4444' }}>
                  {portfolio.netReturn >= 0 ? '+' : ''}{formatCurrency(portfolio.netReturn)}
                </div>
              </div>

              {portfolio.byVehicle.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                    Estimated market value by vehicle
                  </div>
                  <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {portfolio.byVehicle.map(v => (
                      <div key={v.id}
                        onClick={(e) => { e.stopPropagation(); navigate(`/vehicles/${v.id}`); }}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                                 padding: '8px 10px', background: 'var(--surface2)', border: '1px solid var(--border)',
                                 borderRadius: 8, cursor: 'pointer', transition: 'var(--transition)' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(79,142,247,0.12)'; e.currentTarget.style.borderColor = 'rgba(79,142,247,0.4)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface2)'; e.currentTarget.style.borderColor = 'var(--border)'; }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {v.name}
                          </div>
                          {v.reg && <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>{v.reg}</div>}
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700 }}>{formatCurrency(v.value)}</div>
                          {v.unbacked ? (
                            <div style={{ fontSize: 9, color: '#f97316' }}>⚠ no market data</div>
                          ) : v.gainPct !== null ? (
                            <div style={{ fontSize: 10, fontWeight: 600, color: v.gainPct >= 0 ? '#22c55e' : '#ef4444' }}>
                              {v.gainPct >= 0 ? '+' : ''}{v.gainPct.toFixed(1)}%
                            </div>
                          ) : (
                            <div style={{ fontSize: 10, color: 'var(--text3)' }}>no purchase price</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {portfolio.unbackedCount > 0 && (
                <div style={{ fontSize: 11, color: '#f97316', marginTop: 10 }}>
                  ⚠ {portfolio.unbackedCount} valuation{portfolio.unbackedCount !== 1 ? 's' : ''} not backed by market data
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};